import { decodeJwt } from "jose";
import { z } from "zod";

const ACCESS_TOKEN_ROUTE = "/api/auth/access-token";
const EAGER_AUTH_COOKIE = "workos-access-token";
const REFRESH_TIMEOUT_MS = 15_000;
const REFRESH_FAILED = "Failed to refresh access token";
const UNAUTHORIZED = 401;
const EXPIRY_BUFFER_SECONDS = 60;
const SHORT_TOKEN_LIFETIME_SECONDS = 300;
const SHORT_TOKEN_BUFFER_SECONDS = 30;
const CLOCK_SKEW_TOLERANCE_SECONDS = 30;

const routeAnswer = z.object({
  accessToken: z.string().min(1).optional(),
  error: z.string().optional(),
});

export type TokenRouteFetch = (url: string, init: RequestInit) => Promise<Response>;

interface AccessTokenSourceOptions {
  fetchRoute?: TokenRouteFetch;
  nowMs?: () => number;
  readEagerAuthCookie?: () => string | undefined;
}

export interface CookieJar {
  read: () => string;
  write: (cookie: string) => void;
  secure: boolean;
}

function documentCookieJar(): CookieJar | undefined {
  if (typeof document === "undefined") return undefined;
  return {
    read: () => document.cookie,
    write: (cookie) => {
      document.cookie = cookie;
    },
    secure: window.location.protocol === "https:",
  };
}

export function consumeEagerAuthCookie(jar: CookieJar | undefined = documentCookieJar()): string | undefined {
  if (!jar) return undefined;
  const prefix = `${EAGER_AUTH_COOKIE}=`;
  const entries = jar
    .read()
    .split(";")
    .map((cookie) => cookie.trim())
    .filter((cookie) => cookie.startsWith(prefix));
  if (entries.length === 0) return undefined;
  const expire = `${EAGER_AUTH_COOKIE}=; SameSite=Lax; Max-Age=0${jar.secure ? "; Secure" : ""}`;
  jar.write(expire);
  jar.write(`${expire}; Path=/`);
  if (entries.length > 1) return undefined;
  try {
    return decodeURIComponent(entries[0]?.slice(prefix.length) ?? "") || undefined;
  } catch {
    return undefined;
  }
}

export class SignedOutError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "SignedOutError";
  }
}

export class AccessTokenSource {
  private token: string | undefined;
  private clockSkewSeconds = 0;
  private eagerCookieRead = false;
  private inFlight: Promise<string> | null = null;
  private identityGeneration = 0;
  private readonly fetchRoute: TokenRouteFetch;
  private readonly nowMs: () => number;
  private readonly readEagerAuthCookie: () => string | undefined;

  constructor(options: AccessTokenSourceOptions = {}) {
    this.fetchRoute = options.fetchRoute ?? ((url, init) => fetch(url, init));
    this.nowMs = options.nowMs ?? Date.now;
    this.readEagerAuthCookie = options.readEagerAuthCookie ?? consumeEagerAuthCookie;
  }

  async getToken(): Promise<string> {
    if (!this.eagerCookieRead) {
      this.eagerCookieRead = true;
      this.token = this.readEagerAuthCookie() ?? this.token;
    }
    if (this.token !== undefined && !this.isExpiring(this.token)) return this.token;
    return this.refresh();
  }

  refresh(): Promise<string> {
    if (this.inFlight) return this.inFlight;
    const request = this.fetchFromRoute(this.identityGeneration).finally(() => {
      if (this.inFlight === request) this.inFlight = null;
    });
    this.inFlight = request;
    return request;
  }

  cachedToken(): string | undefined {
    return this.token;
  }

  clear(): void {
    this.identityGeneration += 1;
    this.token = undefined;
    this.inFlight = null;
    this.clockSkewSeconds = 0;
  }

  private async fetchFromRoute(generation: number): Promise<string> {
    const requestedAt = Math.floor(this.nowMs() / 1000);
    let answer: z.infer<typeof routeAnswer> = {};
    let status = 0;
    try {
      const response = await this.fetchRoute(ACCESS_TOKEN_ROUTE, {
        cache: "no-store",
        credentials: "same-origin",
        redirect: "error",
        signal: AbortSignal.timeout(REFRESH_TIMEOUT_MS),
        headers: { accept: "application/json" },
      });
      status = response.status;
      answer = routeAnswer.safeParse(await response.json().catch(() => undefined)).data ?? {};
    } catch {
      throw new Error(REFRESH_FAILED);
    }
    if (status === UNAUTHORIZED) throw new SignedOutError(answer.error ?? REFRESH_FAILED);
    if (status < 200 || status >= 300 || answer.accessToken === undefined) {
      throw new Error(answer.error ?? REFRESH_FAILED);
    }
    if (generation !== this.identityGeneration) throw new Error(REFRESH_FAILED);
    this.calibrateClock(answer.accessToken, requestedAt);
    this.token = answer.accessToken;
    return answer.accessToken;
  }

  private calibrateClock(token: string, requestedAt: number): void {
    const issuedAt = readClaims(token)?.iat;
    if (issuedAt === undefined) return;
    const skew = requestedAt - issuedAt;
    this.clockSkewSeconds = skew > CLOCK_SKEW_TOLERANCE_SECONDS ? skew : 0;
  }

  private isExpiring(token: string): boolean {
    const claims = readClaims(token);
    if (claims?.exp === undefined) return false;
    const now = Math.floor(this.nowMs() / 1000) - this.clockSkewSeconds;
    const lifetime = claims.exp - (claims.iat ?? now);
    const buffer = lifetime <= SHORT_TOKEN_LIFETIME_SECONDS ? SHORT_TOKEN_BUFFER_SECONDS : EXPIRY_BUFFER_SECONDS;
    return claims.exp <= now + buffer;
  }
}

function readClaims(token: string): { exp?: number; iat?: number } | null {
  try {
    const { exp, iat } = decodeJwt(token);
    return { exp, iat };
  } catch {
    return null;
  }
}

export const accessTokenSource = new AccessTokenSource();
